<script setup>
import { computed, useAttrs } from 'vue';

import EnIcon from 'Components/icons/EnIcon.vue';
import EnNavigationIcon from 'Components/icons/navigation/EnNavigationIcon.vue';

import { EnDashboardIconVariants } from 'Components/icons/EnDashboardIconVariants';

const props = defineProps({
  /**
   * Id for testing purpose.
   */
  staticId: String,
  /**
   * Icon to be displayed.
   * Of enum NavigationIcons or Icons.
   */
  icon: String,
  /**
   * Icon class to be displayed.
   * Will be ignored if icon is set.
   * Deprecated.
   */
  iconClass: {
    type: String,
  },
  /**
   * Variant to define styling.
   * Of enum EnDashboardIconVariants.
   *
   * Primary will render a full size, red style.
   * Secondary will render a smaller, grey style.
   * Navigation will render a borderless, smaller style. Small style is automatically applied.
   */
  variant: {
    type: String,
    default: EnDashboardIconVariants.Primary,
  },
  /**
   * Use the smaller size variant.
   */
  small: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the icon is rendered as active with an accent text color.
   */
  active: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);
const attrs = useAttrs();

/**
 * Builds the ID for the button.
 * This can be used to identify the button in tests.
 */
function buildId() {
  if (props.staticId) {
    return `en-dashboard-icon-wrapper-${props.staticId}`;
  }
}

const cssClass = computed(() => {
  let cssClass = `en-dashboard-icon--${props.variant}`;

  if (props.small || props.variant === EnDashboardIconVariants.Navigation) {
    cssClass = `${cssClass} en-dashboard-icon--small`;
  }

  if (props.active) {
    cssClass = `${cssClass} en-dashboard-icon--active`;
  }

  return cssClass;
});

const isSecondary = computed(() => {
  return props.variant === EnDashboardIconVariants.Secondary;
});

</script>

<template>
  <span class="en-dashboard-icon-wrapper">
    <div v-bind="attrs"
         class="en-dashboard-icon"
         :class="cssClass"
         :id="buildId()"
         @click="(e) => emit('click', e)"
    >
      <en-navigation-icon v-if="icon" :icon="icon"></en-navigation-icon>
      <en-icon v-else :class="iconClass" class="icon"></en-icon>
    </div>
  </span>
</template>

<style lang="scss" scoped>
@use 'Styles/helper/variables.scss';

.en-dashboard-icon {
  display: grid;
  border-radius: 8px;
  cursor: pointer;
}

.en-dashboard-icon--primary {
  color: variables.$en-red;
  border: 1px solid variables.$dashboard-recently-view-icon-border;
  background: variables.$dashboard-recently-view-icon-bg;
  font-size: 15px;
  min-height: 35px;
  min-width: 35px;
  padding: 6px;
}

.en-dashboard-icon--secondary {
  color: var(--accent-foreground);
  background: var(--accent);
  border: none;

  min-height: 26px;
  min-width: 26px;
  border-radius: 5px;
  place-items: center;
}

.en-dashboard-icon--navigation {
  color: variables.$dashboard-recently-view-secondary-icon-color;
}

.en-dashboard-icon--small {
  font-size: 12.5px;
  min-height: 28px;
  min-width: 28px;
  padding: 4px;
}

.en-dashboard-icon--active {
  color: variables.$en-red;
}

.en-dashboard-icon-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>